import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { composeUrl } from 'app/core/shared/helper/url.helper';
import {
    Notification,
    NotificationList,
} from 'app/layout/common/notifications/notifications.types';
import {
    catchError,
    empty,
    map,
    Observable,
    of,
    ReplaySubject,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { Response } from 'app/model';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _notifications: ReplaySubject<Notification> =
        new ReplaySubject<Notification>(1);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification> {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(): Observable<Response<Notification>> {
        const url = composeUrl('/v1/WIM/DashboardWIM/notifications');
        return this._httpClient.get<Response<Notification>>(url).pipe(
            tap((notifications) => {
                this._notifications.next(notifications.data);
            }),
            catchError((error) => {
                console.error(
                    '[NotificationsService] Failed to get notifications',
                    error
                );
                return of(error.error as Response<Notification>); // fallback empty array
            })
        );
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: NotificationList): Observable<NotificationList> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .post<NotificationList>('api/common/notifications', {
                        notification,
                    })
                    .pipe(
                        map((newNotification) => {
                            notifications.notificationList.push(
                                newNotification
                            );
                            // Update the notifications with the new notification
                            this._notifications.next(notifications);

                            // Return the new notification from observable
                            return newNotification;
                        })
                    )
            )
        );
    }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .patch<Notification>('api/common/notifications', {
                        id,
                        notification,
                    })
                    .pipe(
                        map((updatedNotification: Notification) => {
                            // Find the index of the updated notification
                            const index =
                                notifications.notificationList.findIndex(
                                    (item) => item.id === id
                                );

                            // Update the notification
                            notifications[index] = updatedNotification;

                            // Update the notifications
                            this._notifications.next(notifications);

                            // Return the updated notification
                            return updatedNotification;
                        })
                    )
            )
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .delete<boolean>('api/common/notifications', {
                        params: { id },
                    })
                    .pipe(
                        map((isDeleted: boolean) => {
                            // Find the index of the deleted notification
                            const index =
                                notifications.notificationList.findIndex(
                                    (item) => item.id === id
                                );

                            // Delete the notification
                            notifications.notificationList.splice(index, 1);

                            // Update the notifications
                            this._notifications.next(notifications);

                            // Return the deleted status
                            return isDeleted;
                        })
                    )
            )
        );
    }

    /**
     * Mark all notifications as read
     */
    markAsRead(id: string): Observable<any> {
        const url = composeUrl(`/v1/WIM/DashboardWIM/notifications/read/${id}`);
        return this._httpClient.put(url, {}).pipe(
            take(1),
            tap((notifications) => {
                this.getAll().subscribe();
            })
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<any> {
        const url = composeUrl('/v1/WIM/DashboardWIM/notifications/read-all');
        return this._httpClient.put(url, {}).pipe(
            take(1),
            tap((notifications) => {
                this.getAll().subscribe();
            })
        );
    }
}
